import { useEffect, useMemo, useState } from 'react';

import StripeDomain from '@/core/lib/new-architecture/domain/stripe.domain';
import Query from '@/core/lib/new-architecture/query';
import { useStripeCheckoutSession } from '@/core/lib/new-architecture/query/stripe.query';
import Store from '@/core/lib/new-architecture/store';

const REFETCH_INTERVAL = 1000;

const useStripeStore = () => {
  const { data: stripeCheckoutSession, error: stripeCheckoutSessionError, isError: isStripeCheckoutSessionError } = useStripeCheckoutSession({});
  const { requestToken, isLoggedIn, isReady } = Store.auth.useRequest();
  const enabledAdlessQuery = !!isLoggedIn && isReady;
  const [localRefetchInterval, setLocalRefetchInterval] = useState<false | number>(REFETCH_INTERVAL);
  const [isUserAdlessReady, setIsUserAdlessReady] = useState<boolean>(false);
  const [totalUserAdlessFetch, setTotalUserAdlessFetch] = useState<number>(0);
  const [isDisplayErrorSection, setIsDisplayErrorSection] = useState<boolean>(false);

  const {
    data: userAdless,
    fetchStatus: userAdlessFetchingStatus,
    isError: isUsAdlessError,
  } = Query.user.useAdless({ token: requestToken, refetchInterval: localRefetchInterval, enabled: enabledAdlessQuery });

  useEffect(
    function waitingForUserAdlessUpdated() {
      const pendingStatus = ['fetching', 'paused'];

      if ((!stripeCheckoutSession && !isStripeCheckoutSessionError) || (!isUsAdlessError && !userAdless?.adless)) return;

      if (isUsAdlessError || isStripeCheckoutSessionError) {
        setLocalRefetchInterval(false);
        setIsUserAdlessReady(true);
        setIsDisplayErrorSection(true);
        return;
      }

      if (pendingStatus.includes(userAdlessFetchingStatus)) return;

      const isUserAdlessUpdatedInSopheos = userAdless.adless?.some(({ transaction_id }) => transaction_id === stripeCheckoutSession.id);

      if (isUserAdlessUpdatedInSopheos) {
        setLocalRefetchInterval(false);
        setIsUserAdlessReady(true);
        return;
      }
      if (totalUserAdlessFetch < 11 && userAdlessFetchingStatus === 'idle') {
        setTotalUserAdlessFetch(previousTotalUserAdlessFetch => previousTotalUserAdlessFetch + 1);
        return;
      }

      setLocalRefetchInterval(false);
      setIsUserAdlessReady(true);
      setIsDisplayErrorSection(true);
    },
    [userAdless, stripeCheckoutSession, userAdlessFetchingStatus, isUsAdlessError, isStripeCheckoutSessionError],
  );

  const stripeCheckoutSessionDomain = useMemo(() => {
    if (!stripeCheckoutSession || !userAdless?.profil.adless) return null;

    return new StripeDomain(stripeCheckoutSession, userAdless.profil.adless);
  }, [stripeCheckoutSession, userAdless]);

  return {
    isStripeCheckoutSessionError,
    isUserAdlessReady,
    nbDaysWithoutAds: stripeCheckoutSessionDomain?.reactivateAdsDate.nbDaysWithoutAds,
    reactivateAdsDateInStringFormat: stripeCheckoutSessionDomain?.reactivateAdsDate.reactivateAdsDateInStringFormat,
    stripeCheckoutSessionError,
    stripeCheckoutSessionId: stripeCheckoutSessionDomain?.stripeCheckoutSessionId,
    stripeCheckoutSessionStatus: stripeCheckoutSessionDomain?.stripeCheckoutSessionStatus,
    stripeTotalAmountInEuros: stripeCheckoutSessionDomain?.stripeAmountTotalInEuro,
    isDisplayErrorSection,
  };
};

export default { useStripeStore };
